import { Space, Switch, Typography } from "antd";
import { BrandKit, BrandKitAsset } from "pages/media/types";
import { useDelete, useNotification } from "@refinedev/core";
import { usePublicMediaAssets, useTranslate } from "#src/hooks";
import { ItemAlignmentFeature } from "../../features/ItemAlignmentFeature";
import { ITEM_TYPES, TemplateType } from "../../../types";
import {
  DEFAULT_COLOR,
  DEFAULT_HEADER_HEIGHT,
  DEFAULT_LOGO_RELATIVE_HEIGHT,
} from "../../../constants";
import {
  computeLogoLayoutForRatio,
  isDefaultPublicFontValid,
  isItemOutsideRelativeItemBounds,
  isTransparentHex,
  loadImageRatio,
} from "../../../helpers";
import {
  resolveColorToBrandkitColorIfNeeded,
  isGradient,
} from "../../../helpers/itemsHelper";
import type { GradientDefinition } from "types";
import { PanelSection } from "../PanelSection";
import { HeaderHeightFeature } from "./features/HeaderHeightFeature";
import { HeaderBackgroundColorFeature } from "./features/HeaderBackgroundColorFeature";
import { HeaderPositionFeature } from "./features/HeaderPositionFeature";
import { ItemCardPreview } from "../../controls/ItemCardPreview";
import { ApplySettingsFeature } from "../../features/ApplySettingsFeature";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import {
  useTemplateEditorStore,
  useTemplateEditorStoreApi,
} from "../../../store/useTemplateEditorStore";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useApplySettings } from "../../../hooks/useApplySettings";
import { useBrandKitEdit } from "pages/media/brandkit/hooks/useBrandkitEdit";
import { useState, useCallback, useRef } from "react";
import { BrandkitAssetUploader } from "../../controls/BrandkitAssetUploader";
import { ChangeAssetButton } from "../../controls/ChangeAssetButton";
import { SettingsDisabledOverlay } from "../components/SettingsDisabledOverlay";
import { useChangeLayer } from "../../../hooks/useChangeLayer";
import { SmallBrandKitPreview } from "pages/media/components/SmallBrandKitPreview/";
import { remapTemplatesAndDefaultsWithBrandKit } from "../../../helpers/remapTemplatesAndDefaultsWithBrandKit";
import { BrandkitChangeModal } from "./features/BrandkitChangeModal";
import { isEqual } from "lodash";
import { pollForBrandKitAsset } from "hooks/useBrandKit";

const HEADER_HEIGHT_OPTIONS = [5, 10, 20];

export enum HeaderPosition {
  HEADER = "header",
  FOOTER = "footer",
}

export const BrandTab = ({
  previewWidth,
  previewHeight,
  brandKit,
}: {
  previewWidth: number;
  previewHeight: number;
  brandKit?: BrandKit;
}) => {
  const t = useTranslate();
  const { open: openNotification } = useNotification();
  const brandKitLogo = brandKit?.assets.find(
    (asset) => asset.purpose === "Logo"
  );

  const palette = brandKit?.palette;
  const brandKitId = brandKit?.id;
  const brandKitName = brandKit?.name;

  const {
    projectId,
    instanceId,
    brandKitData: { refetchBrandKit },
    mediaGenerationSettingsData: {
      mediaAspectRatio,
      mediaGenerationSettings,
      updateMediaGenerationSettings,
    },
    organizationId,
    templatesData,
    presenterRatio,
    storyboardWithSlidesData: { slides },
  } = useTemplateData();

  const { storyboardId, isFlashProject } = templatesData;

  const storeApi = useTemplateEditorStoreApi();
  const { defaultPublicFont } = usePublicMediaAssets();

  const { imageRefs, stagedAssets, setStagedAssets, handleEditSave, saving } =
    useBrandKitEdit(brandKitId);

  const { mutateAsync: deleteAsset, isPending: isDeleting } = useDelete();

  const { logo, header, updateLogo, updateHeader, currentChapter } =
    useTemplateEditorStore((state) => ({
      logo: state.templateState.logo,
      header: state.templateState.header,
      updateLogo: state.updateLogo,
      updateHeader: state.updateHeader,
      currentChapter: state.currentChapter,
    }));

  const { addTemplateUpdate, addBulkTemplateUpdate } = useTemplatesStatusStore(
    (state) => ({
      addTemplateUpdate: state.addTemplateUpdate,
      addBulkTemplateUpdate: state.addBulkTemplateUpdate,
    })
  );

  const { handleApplyToAll } = useApplySettings();
  const { newHighestLayerIndex } = useChangeLayer(
    logo.showLogo ? logo : undefined
  );

  const [showUploader, setShowUploader] = useState(false);
  const isEditingLogo = showUploader && !!brandKitLogo;
  // Covers the whole handleSaveLogo chain (including the refetchBrandKit()
  // call after `saving` already flips false), so the uploader keeps showing
  // its uploading state instead of flashing the empty dropzone before the
  // real asset preview swaps in.
  const [isApplyingLogo, setIsApplyingLogo] = useState(false);

  const [changeBrandKitModalOpen, setChangeBrandKitModalOpen] = useState(false);
  const selectedBrandKitRef = useRef<BrandKit | null>(null);

  const applyLogoFromAsset = useCallback(
    async (logoAsset: { asset_id: string; path: string }) => {
      if (!mediaAspectRatio) return;

      const logoRatio = await loadImageRatio(logoAsset.path);
      let logoHeight = DEFAULT_LOGO_RELATIVE_HEIGHT;
      let logoWidth = (logoHeight * logoRatio) / mediaAspectRatio;
      if (logoWidth > 1) {
        logoWidth = 1;
        logoHeight = mediaAspectRatio / logoRatio;
      }
      const logoY = (DEFAULT_HEADER_HEIGHT - DEFAULT_LOGO_RELATIVE_HEIGHT) / 2;
      const logoX = 0.5 - logoWidth / 2;

      const newLogoState = {
        showLogo: true,
        assetId: logoAsset.asset_id,
        path: logoAsset.path,
        size: { height: logoHeight, width: logoWidth },
        position: { x: logoX, y: logoY },
        layerIndex: newHighestLayerIndex,
      };

      updateLogo(newLogoState);
      addTemplateUpdate();

      return newLogoState;
    },
    [updateLogo, addTemplateUpdate, mediaAspectRatio, newHighestLayerIndex]
  );

  const onSwitchLogoDisplay = (checked: boolean) => {
    // If enabling logo and assetId is not set, populate from brandkit
    if (!logo.assetId && brandKitLogo) {
      if (checked) {
        applyLogoFromAsset(brandKitLogo).catch((error) => {
          console.error("Error applying logo from asset", error);
        });
      }
    } else {
      updateLogo({
        showLogo: checked,
      });
      addTemplateUpdate();
    }
  };

  const headerCurrentPosition =
    header.position.y === 0 ? HeaderPosition.HEADER : HeaderPosition.FOOTER;

  const onChangeHeaderPosition = (value: HeaderPosition) => {
    const currentHeaderY = header.position.y;
    const currentLogoY = logo.position.y;
    const headerHeight = header.size.height;

    const logoOffsetFromHeaderTop = currentLogoY - currentHeaderY;

    let newHeaderY: number;
    let newLogoY: number;

    if (value === HeaderPosition.HEADER) {
      // Moving to top
      newHeaderY = 0;
      newLogoY = newHeaderY + logoOffsetFromHeaderTop;
    } else {
      // Moving to bottom (footer)
      newHeaderY = 1 - headerHeight;
      newLogoY = newHeaderY + logoOffsetFromHeaderTop;
    }

    updateHeader({
      position: {
        x: header.position.x,
        y: newHeaderY,
      },
    });

    updateLogo({
      position: {
        x: logo.position.x,
        y: newLogoY,
      },
    });

    addTemplateUpdate();
  };

  const onChangeHeaderHeight = (relativeHeight: number) => {
    const value = relativeHeight / 100;
    let y = header.position.y;

    if (headerCurrentPosition === HeaderPosition.FOOTER) {
      if (value < header.size.height) {
        const difference = header.size.height - value;
        y += difference;
      } else {
        const difference = value - header.size.height;
        y -= difference;
      }
    }

    const isLogoBiggerThanHeader = logo.size.height > value;
    if (isLogoBiggerThanHeader) {
      const logoAspectRatio = logo.size.width / logo.size.height;
      const smallerLogoWidth = value * logoAspectRatio;

      const logoWidthDifference = logo.size.width - smallerLogoWidth;
      updateLogo({
        size: {
          height: value,
          width: smallerLogoWidth,
        },
        position: {
          x: logo.position.x + logoWidthDifference / 2,
          y: y,
        },
      });
    }

    updateHeader({
      size: {
        width: header.size.width,
        height: value,
      },
      position: {
        x: header.position.x,
        y: y,
      },
    });

    addTemplateUpdate();
  };

  const onChangeHeaderBackgroundColor = (
    color?: string | GradientDefinition
  ) => {
    updateHeader({ background: color, view: "Color" });
    addTemplateUpdate();
  };

  const isChapterType = currentChapter.type === TemplateType.CHAPTER;
  const isChapter = isChapterType && currentChapter.sectionId;

  const updateChaptersLogo = useCallback(
    (newLogo: BrandKitAsset, newLogoRatio: number) => {
      const { templates, setTemplate } = storeApi.getState();
      const currentTemplateId = currentChapter.templateId;

      mediaGenerationSettings?.chapters.forEach((chapter) => {
        const templateId = chapter.chapter_template_id;
        if (
          !templateId ||
          templateId === currentTemplateId ||
          !mediaAspectRatio
        )
          return;

        const template = templates.get(templateId);
        if (!template) return;

        const chapterLogo = template.logo;
        const { size: chapterNewSize, position: chapterNewPosition } =
          computeLogoLayoutForRatio(
            chapterLogo.size,
            chapterLogo.position,
            newLogoRatio,
            mediaAspectRatio
          );

        setTemplate(templateId, {
          ...template,
          logo: {
            ...chapterLogo,
            assetId: newLogo.asset_id,
            path: newLogo.path,
            size: chapterNewSize,
            position: chapterNewPosition,
          },
        });
      });
    },
    [storeApi, currentChapter, mediaGenerationSettings, mediaAspectRatio]
  );

  const handleSaveLogo = useCallback(async () => {
    if (!brandKitName || !palette || !organizationId || !mediaAspectRatio)
      return;

    const isAddingNewLogo = !brandKitLogo;

    // If editing (replacing existing logo), delete the old one first
    if (isEditingLogo && brandKitLogo.asset_id) {
      await deleteAsset({
        resource: `media/${organizationId}/brand_kits/${brandKitId}/assets`,
        id: brandKitLogo.asset_id,
        successNotification: false,
      });
    }

    await handleEditSave(
      { name: brandKitName, colors: palette },
      () => undefined
    );

    // Refetch the brandKit used by TemplateDataProvider (different query than useBrandKitEdit)
    const newLogo = await pollForBrandKitAsset(refetchBrandKit, "Logo");

    if (newLogo) {
      if (isAddingNewLogo) {
        const newLogoState = await applyLogoFromAsset(newLogo);
        handleApplyToAll({ logo: { ...logo, ...newLogoState } });
        addBulkTemplateUpdate();
      } else {
        const newLogoRatio = await loadImageRatio(newLogo.path);

        const { size: newSize, position: newPosition } =
          computeLogoLayoutForRatio(
            logo.size,
            logo.position,
            newLogoRatio,
            mediaAspectRatio
          );

        updateLogo({
          assetId: newLogo.asset_id,
          path: newLogo.path,
          size: newSize,
          position: newPosition,
        });

        // Update all other chapters individually, preserving each chapter's logo position and height
        updateChaptersLogo(newLogo, newLogoRatio);
      }
      addTemplateUpdate();
      openNotification?.({
        type: "success",
        message: t(
          "components.templates.settings.brandingTab.logoAppliedToAll"
        ),
      });
    }
  }, [
    brandKitName,
    palette,
    organizationId,
    brandKitLogo,
    isEditingLogo,
    deleteAsset,
    handleEditSave,
    refetchBrandKit,
    applyLogoFromAsset,
    logo,
    updateLogo,
    addTemplateUpdate,
    mediaAspectRatio,
    handleApplyToAll,
    updateChaptersLogo,
    brandKitId,
    addBulkTemplateUpdate,
    openNotification,
    t,
  ]);

  const resolvedHeaderBackground = resolveColorToBrandkitColorIfNeeded(
    header.background,
    palette
  );
  const headerPicture = header.view === "Picture" && !!header.banner?.assetId;
  const hasHeader =
    headerPicture ||
    isGradient(resolvedHeaderBackground) ||
    (!!resolvedHeaderBackground && !isTransparentHex(resolvedHeaderBackground));

  const handleSwitchHeaderDisplay = (checked: boolean) => {
    const { isOutsideVertically } = isItemOutsideRelativeItemBounds(
      logo.position,
      logo.size,
      header
    );

    updateHeader({
      background: checked
        ? (palette?.secondary_color ?? DEFAULT_COLOR)
        : undefined,
      view: header.banner && checked ? "Picture" : "Color",
      layerIndex: logo.layerIndex,
    });

    // Adjust logo as well if needed
    if (checked) {
      const size = { ...logo.size };
      if (size.height > header.size.height) {
        size.height = header.size.height;
        size.width = (size.width * header.size.height) / logo.size.height;
      }

      let y = logo.position.y;
      if (isOutsideVertically) {
        y = header.position.y + header.size.height / 2 - size.height / 2;
      }

      let layerIndex = logo.layerIndex;
      if (logo.layerIndex < header.layerIndex) {
        layerIndex = header.layerIndex;
      }

      const hasSizeChanged =
        size.height !== logo.size.height || size.width !== logo.size.width;
      const hasPositionChanged = y !== logo.position.y;
      const hasLayerChanged = layerIndex !== logo.layerIndex;

      if (hasSizeChanged || hasPositionChanged || hasLayerChanged) {
        updateLogo({
          size,
          position: {
            x: logo.position.x,
            y,
          },
          layerIndex,
        });
      }
    }

    addTemplateUpdate();
  };

  const uploader = () => {
    return (
      <BrandkitAssetUploader
        mimeType="image"
        purpose="Logo"
        brandKitAssets={brandKit?.assets}
        stagedAssets={stagedAssets}
        setStagedAssets={setStagedAssets}
        organizationId={organizationId}
        uploadRef={imageRefs.Logo}
        brandKitId={brandKitId}
        onSave={() => {
          setIsApplyingLogo(true);
          handleSaveLogo()
            .then(() => {
              setShowUploader(false);
              setStagedAssets([]);
            })
            .catch((error) => {
              console.error("Error saving logo", error);
              setStagedAssets([]);
              imageRefs.Logo.current?.reset();
            })
            .finally(() => setIsApplyingLogo(false));
        }}
        isSaving={saving || isApplyingLogo}
        isDeleting={isDeleting}
        isEditing={isEditingLogo}
        onCancel={() => setShowUploader(false)}
      />
    );
  };

  const remapWithBrandKit = useCallback(
    async (selectedBrandKit: BrandKit) => {
      const selectedLogoPath = selectedBrandKit.assets.find(
        (asset) => asset.purpose === "Logo"
      )?.path;
      const freshLogoRatio = selectedLogoPath
        ? await loadImageRatio(selectedLogoPath).catch(() => undefined)
        : undefined;

      remapTemplatesAndDefaultsWithBrandKit(selectedBrandKit, {
        mediaGenerationSettings,
        mediaAspectRatio,
        templatesData: {
          presenter: templatesData.presenter,
          contact: templatesData.contact,
        },
        defaultPublicFont: isDefaultPublicFontValid(defaultPublicFont)
          ? defaultPublicFont
          : undefined,
        presenterRatio,
        logoRatio: freshLogoRatio,
        storeApi,
        isFlashProject,
        slides,
        updateMediaGenerationSettings,
      });
    },
    [
      defaultPublicFont,
      isFlashProject,
      mediaAspectRatio,
      mediaGenerationSettings,
      presenterRatio,
      slides,
      storeApi,
      templatesData.contact,
      templatesData.presenter,
      updateMediaGenerationSettings,
    ]
  );

  const refreshBrandKit = useCallback(async () => {
    // Same BK closed → it may have been edited in place. Refetch the detail
    // (the form only invalidates the BK list, not this query) and remap only
    // if the BK actually changed.
    const result = await refetchBrandKit();
    const fresh = result.data?.data;
    if (fresh && !isEqual(fresh, brandKit)) {
      await remapWithBrandKit(fresh);
      addBulkTemplateUpdate();
    }
  }, [addBulkTemplateUpdate, brandKit, refetchBrandKit, remapWithBrandKit]);

  const handleBrandKitModalClose = useCallback(async () => {
    const selected = selectedBrandKitRef.current;
    selectedBrandKitRef.current = null;
    setChangeBrandKitModalOpen(false);

    if (selected && selected.id !== brandKit?.id) {
      // Different BK picked → remap with the selected one.
      await remapWithBrandKit(selected);
      addBulkTemplateUpdate();
      return;
    }

    await refreshBrandKit();
  }, [addBulkTemplateUpdate, brandKit, remapWithBrandKit, refreshBrandKit]);

  const handleBrandKitModalCancel = useCallback(() => {
    selectedBrandKitRef.current = null;
    setChangeBrandKitModalOpen(false);
    refreshBrandKit().catch((error) => {
      console.error("Error refreshing brand kit", error);
    });
  }, [refreshBrandKit]);

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.brandingTab.title")}
        subtitle={t("components.templates.settings.brandingTab.description")}
        primary
      />
      {brandKit && (
        <PanelSection
          title={t("components.templates.settings.brandTab.brandKit")}
        >
          <ItemCardPreview
            item={t(
              "components.templates.settings.brandTab.brandKit"
            ).toLowerCase()}
            title={brandKit.name}
            customPreview={
              <SmallBrandKitPreview brandKit={brandKit} showName={false} />
            }
            onClick={() => setChangeBrandKitModalOpen(true)}
          />
        </PanelSection>
      )}

      {brandKitLogo ? (
        <>
          <PanelSection
            title={t("components.templates.settings.brandingTab.logo")}
          >
            {showUploader ? (
              uploader()
            ) : (
              <ItemCardPreview
                item={t(
                  "components.templates.settings.brandingTab.title"
                ).toLowerCase()}
                title={brandKitLogo.name}
                cover={{
                  path: brandKitLogo.path,
                  assetId: brandKitLogo.asset_id,
                }}
                editButton={
                  <ChangeAssetButton
                    buttonItem={t(
                      "components.templates.settings.brandingTab.logo"
                    )}
                    popConfirmProps={{
                      title: t(
                        "components.templates.settings.brandingTab.replaceLogo.title"
                      ),
                      description: t(
                        "components.templates.settings.brandingTab.replaceLogo.description"
                      ),
                      onConfirm: () => setShowUploader(true),
                    }}
                    style={{ width: "100%" }}
                  />
                }
              />
            )}
            {(isChapter || (isFlashProject && isChapterType)) && (
              <Space align="center">
                <Switch
                  size="small"
                  checked={logo.showLogo}
                  onChange={onSwitchLogoDisplay}
                  disabled={isEditingLogo}
                />
                <Typography.Text>
                  {t("components.templates.settings.brandingTab.showLogo")}
                </Typography.Text>
              </Space>
            )}
          </PanelSection>
          {logo.showLogo &&
            (isChapter || (isFlashProject && isChapterType)) && (
              <SettingsDisabledOverlay isDisabled={isEditingLogo}>
                <PanelSection
                  title={t(
                    "components.templates.settings.brandingTab.logoAlignment"
                  )}
                >
                  <ItemAlignmentFeature
                    pickedItem={logo}
                    relativeItem={hasHeader ? header : undefined}
                    onChange={(value) => {
                      updateLogo({
                        ...value,
                        size: {
                          width: logo.size.width,
                          height: logo.size.height,
                        },
                      });
                      addTemplateUpdate();
                    }}
                    previewWidth={previewWidth}
                    previewHeight={previewHeight}
                  />
                </PanelSection>
                <PanelSection
                  title={t(
                    "components.templates.settings.brandingTab.displayLogoBackground"
                  )}
                  switchProps={{
                    checked: hasHeader,
                    onChange: handleSwitchHeaderDisplay,
                  }}
                />
                {hasHeader && (
                  <>
                    <PanelSection
                      title={t(
                        "components.templates.settings.brandingTab.headerPosition"
                      )}
                    >
                      <HeaderPositionFeature
                        position={headerCurrentPosition}
                        onChange={onChangeHeaderPosition}
                      />
                    </PanelSection>
                    <PanelSection
                      title={t(
                        "components.templates.settings.brandingTab.logoBannerHeight"
                      )}
                    >
                      <HeaderHeightFeature
                        label={t(
                          "components.templates.settings.brandingTab.radioButtonLabels.custom"
                        )}
                        value={header.size.height * 100}
                        options={HEADER_HEIGHT_OPTIONS}
                        onChange={onChangeHeaderHeight}
                      />
                    </PanelSection>
                    <PanelSection
                      title={t(
                        "components.templates.settings.brandingTab.headerBackgroundColor"
                      )}
                    >
                      <HeaderBackgroundColorFeature
                        background={header.background}
                        palette={palette}
                        onChange={onChangeHeaderBackgroundColor}
                      />
                    </PanelSection>
                  </>
                )}
              </SettingsDisabledOverlay>
            )}
          <PanelSection>
            <ApplySettingsFeature
              partialTemplateState={{ logo, header }}
              itemType={ITEM_TYPES.Logo}
              // Custom projects only render logos on chapter scenes, so the type limit only matters on Flash.
              allSceneTypes={isFlashProject}
            />
          </PanelSection>
        </>
      ) : (
        <PanelSection
          title={t("components.templates.settings.brandingTab.noLogo.title")}
          subtitle={t(
            "components.templates.settings.brandingTab.noLogo.description"
          )}
        >
          {uploader()}
        </PanelSection>
      )}

      <BrandkitChangeModal
        projectId={projectId}
        storyboardId={storyboardId ?? undefined}
        instanceId={instanceId}
        brandKitId={brandKit?.id}
        open={changeBrandKitModalOpen}
        selectedBrandKitRef={selectedBrandKitRef}
        onCancel={handleBrandKitModalCancel}
        onClose={() => {
          handleBrandKitModalClose().catch((error) => {
            console.error("Error remapping brand kit", error);
          });
        }}
      />
    </>
  );
};
